import { useAemConfig } from '@as-react/common-components';
import { useGetSearchContentSuggestionQueryOptions, useGetSearchSuggestionsQueryOptions, } from '@as-react/common-queries';
import { MenuSearchResultBlockVariant, } from '@as-react/common-types';
import { useTranslation } from 'react-i18next';
import { DELAY } from '../constants/GeneralConstants';
import { I18N_NAMESPACES } from '../constants/i18nConstants';
import { SEARCH_RESULT_LIMIT } from '../constants/SearchConstants';
import { hasItems } from '../util/ArrayUtil';
import { getBalancedBrandsAndCategoriesLimits, getElasticSearchIndexWithEnv, getSearchBrands, getSearchCategories, getSearchContent, getSearchKeywords, getSearchProducts, getSearchResultLimit, hasSearches, wrapSubtitleWithTranslation, } from '../util/SearchUtil';
import { useDebouncedQuery } from './useDebouncedQuery';
const COMBOBOX_ID = 'menu-search';
export const getOptionLabel = (option) => {
    if (!option)
        return '';
    if (option.variant === MenuSearchResultBlockVariant.PRODUCTS) {
        return option.data.title;
    }
    const { name, subtitle } = option.data;
    const subtitleText = subtitle && subtitle.length > 0 ? subtitle.slice(0, 2).join(', ') : '';
    return `${name} ${subtitleText}`.trim();
};
export const useNewMenuSearch = ({ searchTerm = '', suggestionsParentLink, brandsParentLink, categoriesParentLink, }) => {
    const { t } = useTranslation(I18N_NAMESPACES.navigation);
    const aemConfiguration = useAemConfig();
    const { defaultRequestParams, activeEnvironment, elasticsearchIndex } = aemConfiguration;
    const index = getElasticSearchIndexWithEnv(activeEnvironment, elasticsearchIndex);
    const maxAmountEnhancedItems = getSearchResultLimit(searchTerm);
    const selectSuggestions = (data) => ({
        keywords: getSearchKeywords(data === null || data === void 0 ? void 0 : data.keywords, suggestionsParentLink),
        brands: getSearchBrands(data === null || data === void 0 ? void 0 : data.brands, brandsParentLink),
        categories: getSearchCategories(data === null || data === void 0 ? void 0 : data.categories, categoriesParentLink),
        products: getSearchProducts(data === null || data === void 0 ? void 0 : data.products, aemConfiguration.root),
    });
    const selectContentSuggestions = (data) => ({
        content: getSearchContent(data === null || data === void 0 ? void 0 : data.content),
    });
    const { data: { keywords = [], brands = [], categories = [], products = [] } = {} } = useDebouncedQuery(useGetSearchSuggestionsQueryOptions({ query: searchTerm, mainWebshop: defaultRequestParams.mainWebshop, language: defaultRequestParams.language }, undefined, {
        query: {
            enabled: Boolean(searchTerm),
            retry: false,
            placeholderData: prev => prev,
            select: selectSuggestions,
        },
    }), DELAY.DELAY_250);
    const { data: { content = [] } = {} } = useDebouncedQuery(useGetSearchContentSuggestionQueryOptions({ q: searchTerm, index }, undefined, {
        query: {
            enabled: Boolean(searchTerm),
            retry: false,
            placeholderData: prev => prev,
            select: selectContentSuggestions,
        },
    }), DELAY.DELAY_250);
    const searchesKeywords = keywords.slice(0, SEARCH_RESULT_LIMIT.FOR_SEARCH_SUGGESTIONS);
    const cappedBrands = brands.slice(0, maxAmountEnhancedItems);
    const cappedCategories = categories.slice(0, maxAmountEnhancedItems);
    const { brandsLimit, categoriesLimit } = getBalancedBrandsAndCategoriesLimits(cappedBrands.length, cappedCategories.length, SEARCH_RESULT_LIMIT.FOR_SEARCH_BRANDS_OR_CATEGORIES);
    const searchesBrandOrCat = hasItems(keywords) || hasSearches(categories, brands)
        ? [
            ...cappedBrands.slice(0, brandsLimit),
            ...cappedCategories.slice(0, categoriesLimit).map(item => wrapSubtitleWithTranslation(item, true, t)),
        ]
        : [];
    const searchesProducts = products.slice(0, SEARCH_RESULT_LIMIT.FOR_SEARCH_PRODUCTS);
    const contentItems = content.slice(0, maxAmountEnhancedItems);
    const hasSuggestionResults = hasItems(searchesKeywords) || hasItems(searchesBrandOrCat);
    const hasContentResults = hasItems(contentItems);
    const hasProductResults = hasItems(searchesProducts);
    const hasSearchResults = hasSuggestionResults || hasProductResults || hasContentResults;
    let flatIndexCursor = 0;
    const nextFlatIndex = () => flatIndexCursor++;
    const keywordOptions = searchesKeywords.map((data, i) => ({
        optionId: `${COMBOBOX_ID}-option-searches-keyword-${i}`,
        flatIndex: nextFlatIndex(),
        variant: MenuSearchResultBlockVariant.SUGGESTIONS,
        data,
    }));
    const searchesBrandOrCatOptions = searchesBrandOrCat.map((data, i) => ({
        optionId: `${COMBOBOX_ID}-option-searches-item-${i}`,
        flatIndex: nextFlatIndex(),
        variant: MenuSearchResultBlockVariant.BRAND_OR_CAT,
        data,
    }));
    const productOptions = searchesProducts.map((data, i) => ({
        optionId: `${COMBOBOX_ID}-option-products-item-${i}`,
        flatIndex: nextFlatIndex(),
        variant: MenuSearchResultBlockVariant.PRODUCTS,
        data,
    }));
    const contentOptions = contentItems.map((data, i) => ({
        optionId: `${COMBOBOX_ID}-option-content-item-${i}`,
        flatIndex: nextFlatIndex(),
        variant: MenuSearchResultBlockVariant.CONTENT,
        data,
    }));
    const flatOptions = [
        ...keywordOptions,
        ...searchesBrandOrCatOptions,
        ...productOptions,
        ...contentOptions,
    ];
    return {
        hasSearchResults,
        hasSuggestionResults,
        hasProductResults,
        hasContentResults,
        contentItems,
        keywordOptions,
        searchesBrandOrCatOptions,
        productOptions,
        contentOptions,
        flatOptions,
    };
};
